import { heicTo, isHeic } from "heic-to"

/**
 * Проверяет, является ли URL изображением в формате HEIC
 * @param {string} url - URL изображения
 * @returns {boolean}
 */
export function isHeicByUrl(url) {
  if (!url) return false
  try {
    const urlObj = new URL(url, window.location.origin)
    const pathname = urlObj.pathname.toLowerCase()
    return /\.(heic|heif)$/i.test(pathname)
  } catch {
    return false
  }
}

/**
 * Проверяет расширение файла
 * @param {string} filename - Имя файла
 * @returns {string} - Расширение файла
 */
function getFileExtension(filename) {
  if (!filename || !filename.includes('.')) {
    return ''
  }
  return filename.split('.').pop().toLowerCase()
}

/**
 * Проверяет, является ли HEIC на основе расширения или сигнатуры
 * @param {string} filename - Имя файла
 * @returns {boolean}
 */
export function shouldConvertHeic(filename) {
  const extension = getFileExtension(filename)
  return ['heic', 'heif'].includes(extension)
}

/**
 * Конвертирует HEIC изображение в JPEG
 * @param {Blob} blob - Blob HEIC изображения
 * @returns {Promise<Blob>} - Promise с JPEG blob
 */
export async function convertHeicToJpeg(blob) {
  try {
    return await heicTo({
      blob: blob,
      type: "image/jpeg",
      quality: 0.9
    })
  } catch (error) {
    console.error('Error converting HEIC to JPEG:', error)
    throw error
  }
}

/**
 * Загружает изображение и конвертирует его если это HEIC
 * @param {string} url - URL изображения
 * @param {string} contentType - Content-Type изображения
 * @returns {Promise<string>} - Promise с URL изображения (может быть объект или исходный URL)
 */
export async function loadAndConvertImage(url, contentType) {
  if (!url) {
    return null
  }

  // Проверяем по Content-Type
  if (contentType && contentType === 'image/heic') {
    try {
      const response = await fetch(url)
      const blob = await response.blob()
      const convertedBlob = await convertHeicToJpeg(blob)
      return URL.createObjectURL(convertedBlob)
    } catch (error) {
      console.error('Failed to convert HEIC from URL:', url, error)
      return url // Возвращаем исходный URL если конвертация не сработала
    }
  }

  // Проверяем по URL
  if (isHeicByUrl(url)) {
    try {
      const response = await fetch(url)
      const blob = await response.blob()
      
      // Дополнительно проверяем по blob
      if (blob.type === 'image/heic' || await isHeic(blob)) {
        const convertedBlob = await convertHeicToJpeg(blob)
        return URL.createObjectURL(convertedBlob)
      }
    } catch (error) {
      console.error('Failed to convert HEIC from URL:', url, error)
      return url
    }
  }

  return url
}
